import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import heroImage from "../assets/zeamays.png";
import { useAuth } from "../context/AuthContext";
import { isAdminUser } from "../services/admin";
import { getBlogPost } from "../services/blog";
import SEO, { SITE_NAME, SITE_URL } from "../components/SEO";

const formatDate = (dateValue) => {
  if (!dateValue) return "";

  return new Intl.DateTimeFormat("en-NG", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(dateValue));
};

const BlogPost = () => {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadPost = async () => {
      if (authLoading) {
        return;
      }

      setLoading(true);
      setError("");

      try {
        const adminAccess = user ? await isAdminUser(user) : false;
        const blogPost = await getBlogPost(id, { includeUnpublished: adminAccess });

        if (!cancelled) {
          setPost(blogPost);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Unable to load this blog post.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPost();

    return () => {
      cancelled = true;
    };
  }, [authLoading, id, user]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#f7fbef] px-4 py-28 text-center text-slate-700">
        <p className="text-lg font-semibold">Loading article...</p>
      </main>
    );
  }

  if (error || !post) {
    return (
      <main className="min-h-screen bg-[#f7fbef] px-4 py-28 text-center text-slate-700">
        <div className="mx-auto max-w-xl rounded-lg border border-red-200 bg-white p-8">
          <h1 className="text-2xl font-bold text-red-700">Article not found</h1>
          <p className="mt-3 text-slate-600">{error || "This article is not available."}</p>
          <Link to="/blog" className="mt-6 inline-flex rounded-md bg-red-700 px-4 py-2 font-semibold text-white">
            Back to Blog
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#f7fbef] text-slate-900">
      <SEO
        title={`${post.title} | ${SITE_NAME}`}
        description={(post.excerpt || post.content || "Read the latest Zea Mays Foods story.").slice(0, 160)}
        path={`/blog/${post.id}`}
        image={post.featured_image || undefined}
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt || post.content || "",
          image: post.featured_image || `${SITE_URL}/zeamays-favicon.png`,
          datePublished: post.created_at,
          author: { "@type": "Organization", name: SITE_NAME },
          publisher: { "@type": "Organization", name: SITE_NAME },
          mainEntityOfPage: `${SITE_URL}/blog/${post.id}`,
        }}
      />
      <article className="mx-auto max-w-4xl px-4 py-28 sm:px-6 lg:px-8">
        <Link
          to="/blog"
          className="inline-flex rounded-md border border-lime-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-lime-50"
        >
          Back to Blog
        </Link>

        <div className="mt-8 overflow-hidden rounded-lg border border-lime-200 bg-white shadow-sm">
          <div className="overflow-hidden bg-lime-100">
            <img
              src={post.featured_image || heroImage}
              alt={post.featured_image ? post.title : ""}
              className="mx-auto max-h-[560px] w-full object-contain"
              width="960"
              height="540"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </div>
          <div className="p-6 sm:p-8">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold text-red-700">{formatDate(post.created_at)}</p>
              {!post.published ? (
                <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">
                  Draft preview
                </span>
              ) : null}
            </div>
            <h1 className="mt-3 text-3xl font-bold leading-tight text-slate-950 sm:text-5xl">
              {post.title}
            </h1>
            {post.excerpt ? (
              <p className="mt-5 text-lg leading-8 text-slate-600">{post.excerpt}</p>
            ) : null}
            <div className="mt-8 whitespace-pre-line text-base leading-8 text-slate-700 sm:text-lg">
              {post.content}
            </div>
          </div>
        </div>
      </article>
    </main>
  );
};

export default BlogPost;
